import { Body, Button, ButtonGroup, H2, H3 } from "@datapay-by-datacom/design-system";
import { useNavigate } from "react-router-dom";
import { Pagination } from "../components/Pagination/Pagination";
import {
  DEFAULT_PAGE_SIZE,
  PAGE_SIZE_OPTIONS,
  PAY_COMPONENT_IMPORT_BASE_PATH,
  clampPageSize,
} from "../config";
import { usePagination } from "../hooks/usePagination";
import { useWizard } from "../hooks/useWizard";
import { clearWizardSession } from "../utils/wizardSession";
import styles from "./ValidationErrorsScreen.module.css";

/**
 * Validation Errors screen — shown when `ImportResult.status === "invalid"`.
 *
 * Renders a table of row-level validation errors returned by the API.
 * Nothing was committed — the API does not partially commit on `invalid`.
 *
 * The primary action is "Re-upload CSV" which navigates back to the Upload
 * screen so the PS user can correct their file and try again.
 */
export interface ValidationErrorsScreenProps {
  redesignEnabled?: boolean;
}

export function ValidationErrorsScreen({
  redesignEnabled = false,
}: ValidationErrorsScreenProps) {
  const { state, updateState } = useWizard();
  const navigate = useNavigate();

  const errors = state.importResult?.errors ?? [];
  const pageSize = state.pageSize ?? DEFAULT_PAGE_SIZE;
  const { page, setPage, pageCount, pageItems, total } = usePagination(
    errors,
    pageSize,
  );
  const showPager = total > PAGE_SIZE_OPTIONS[0];

  const handleReUpload = () => {
    clearWizardSession();
    // Clear the import result so the upload screen starts fresh, but keep
    // options (providerId) so the user doesn't need to re-enter them.
    updateState({ importResult: null, file: null, csvText: null, preview: null });
    void navigate(PAY_COMPONENT_IMPORT_BASE_PATH);
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <H2>Validation Errors</H2>
        <Body>
          The import could not be completed. The CSV contains the following
          validation errors. No data was written. Please correct your file and
          re-upload.
        </Body>
      </div>

      {errors.length === 0 ? (
        <div className={styles.emptyState} role={redesignEnabled ? "status" : undefined}>
          <Body>No validation errors found.</Body>
        </div>
      ) : (
        <section
          className={styles.errorsSection}
          aria-labelledby="errors-heading"
        >
          <H3 id="errors-heading">
            {errors.length} error{errors.length !== 1 ? "s" : ""} found
          </H3>
          <div
            className={styles.tableWrapper}
            role="region"
            aria-label="Validation errors table"
            tabIndex={0}
          >
            <table className={styles.errorsTable}>
              <thead>
                <tr>
                  <th scope="col" className={styles.th}>
                    Row
                  </th>
                  <th scope="col" className={styles.th}>
                    Employee Code
                  </th>
                  <th scope="col" className={styles.th}>
                    Component
                  </th>
                  <th scope="col" className={styles.th}>
                    Error
                  </th>
                </tr>
              </thead>
              <tbody>
                {pageItems.map((err, index) => (
                  <tr key={index} className={styles.errorRow}>
                    <td className={styles.tdRow}>{err.row}</td>
                    <td className={styles.tdEmployee}>{err.employeeCode}</td>
                    <td className={styles.tdComponent}>{err.componentCode}</td>
                    <td className={styles.tdError}>{err.error}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {showPager && (
            <Pagination
              page={page}
              pageCount={pageCount}
              total={total}
              pageSize={pageSize}
              onChange={setPage}
              onPageSizeChange={(n) => updateState({ pageSize: clampPageSize(n) })}
            />
          )}
        </section>
      )}

      <ButtonGroup align="right">
        <Button variant="primary" onPress={handleReUpload}>
          Re-upload CSV
        </Button>
      </ButtonGroup>
    </div>
  );
}
